import { ISetting } from '@biolibtech/biolib-js';

export enum SequenceType {
    EMPTY = 'empty',
    PROTEIN = 'protein',
    DNA = 'dna',
    RNA = 'rna',
}

export interface ISequenceRecord {
    sequence: string;
    sequenceId: string;
    description: string;
}

const DEFAULT_MAX_SEQUENCE_NAME_LENGTH = 128;

class ValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ValidationError';
    }
}

function findInvalidSequenceIdCharacters(sequence: string): string[] {
    const allowedSequenceIdChars = new Set(
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789|-_.:*#,=+()'.split(''),
    );
    return Array.from(sequence).filter((char) => !allowedSequenceIdChars.has(char));
}

const SEQUENCE_TYPE_SETS: Record<SequenceType, string> = {
    [SequenceType.EMPTY]: '',
    [SequenceType.PROTEIN]: 'acdefghiklmnpqrstvwybxzACDEFGHIKLMNPQRSTVWYBXZ',
    [SequenceType.DNA]: 'ATCGatcg',
    [SequenceType.RNA]: 'AUCGaucg',
};

function findInvalidSequenceCharacters(
    sequence: string,
    config?: { sequence_type?: SequenceType; additional_sequence_characters?: string; allow_empty_sequence?: boolean },
): string[] {
    let allowedCharacters: string = 'lagvsertidpkqnfymhwcxbuzoLAGVSERTIDPKQNFYMHWCXBUZ';

    if (config?.sequence_type) {
        allowedCharacters = SEQUENCE_TYPE_SETS[config.sequence_type];
    }

    if (config?.additional_sequence_characters) {
        allowedCharacters += config.additional_sequence_characters;
    }

    const allowedSequenceChars = new Set(allowedCharacters.split(''));
    return Array.from(sequence).filter((char) => !allowedSequenceChars.has(char));
}

export function parseFasta(
    data: string,
    config?: { sequence_type?: SequenceType; additional_sequence_characters?: string; allow_empty_sequence?: boolean; auto_uppercase_sequence?: boolean; max_sequence_name_length?: number },
    validateSequencesAndIds: boolean = true,
): ISequenceRecord[] {
    const sequences: string[] = [];
    let currentSequence: string[] = [];
    data.split('\n').forEach((line) => {
        if (line.startsWith('>')) {
            if (currentSequence.length) {
                sequences.push(currentSequence.join('\n'));
            }
            currentSequence = [`${line.substring(1).trim()}\n`];
        } else {
            const trimmedLine = line.trim();
            if (trimmedLine) {
                currentSequence.push(`${trimmedLine}\n`);
            }
        }
    });
    if (currentSequence.length) {
        sequences.push(currentSequence.join(''));
    }

    const parsedSequences: ISequenceRecord[] = [];
    for (const sequenceData of sequences) {
        const lines = sequenceData.trim().split('\n');
        const headerLine = lines.shift()!.trim().split(/\s+/); // Assert non-null with `!`
        const sequenceId = headerLine[0];
        const description = headerLine.slice(1).join(' ');
        const shouldUppercase = config?.auto_uppercase_sequence !== false;
        const sequence = lines.map((seq) => shouldUppercase ? seq.trim().toUpperCase() : seq.trim()).join('');

        const maxNameLength = config?.max_sequence_name_length ?? DEFAULT_MAX_SEQUENCE_NAME_LENGTH;
        if (sequenceId.length > maxNameLength) {
            throw new ValidationError(`Name of sequence ${sequenceId} is too long (max ${maxNameLength} characters)`);
        }

        if (validateSequencesAndIds) {
            const invalidSequenceIdChars = findInvalidSequenceIdCharacters(sequenceId);
            if (invalidSequenceIdChars.length > 0) {
                throw new ValidationError(
                    `Invalid character ("${invalidSequenceIdChars[0]}") found in ID of sequence ${sequenceId}`,
                );
            }

            const invalidSequenceChars = findInvalidSequenceCharacters(sequence, config);
            if (invalidSequenceChars.length > 0) {
                throw new ValidationError(
                    `Invalid character ("${invalidSequenceChars[0]}") found in sequence ${sequenceId}`,
                );
            }
        }

        if (sequence.length === 0 && !config?.allow_empty_sequence) {
            throw new ValidationError(`No sequence found for entry "${sequenceId}"`);
        }

        parsedSequences.push({ description, sequence, sequenceId });
    }

    return parsedSequences;
}

export function validateSequenceInputRows(
    rows: Record<number, Record<number, string>>,
    setting: ISetting,
): null | string {
    const nameToRowNumber: Record<string, number> = {};
    let isEmpty = true;
    
    for (const [rowIndex, columns] of Object.entries(rows)) {
        const rowNumber = Number(rowIndex) + 1;
        const nameInput = columns[0].trim();
        const sequence = columns[1].trim();
        
        if (nameInput || sequence) {
            isEmpty = false;
        }
        
        const name = nameInput.replace('>', '') || `sequence_${rowNumber.toString()}`;
        
        if (nameInput && !sequence && !setting.config?.allow_empty_sequence) {
            return `Please enter a sequence for "${name}".`;
        }

        const maxNameLength = setting.config?.max_sequence_name_length ?? DEFAULT_MAX_SEQUENCE_NAME_LENGTH;
        if (name.length > maxNameLength) {
            return `Name "${name}" is too long (max ${maxNameLength} characters)`;
        }

        const invalidNameChars = findInvalidSequenceIdCharacters(name);
        if (invalidNameChars.length > 0) {
            return `Name "${name}" contains invalid character ("${invalidNameChars[0]}")`;
        }

        if (sequence) {
            if (/\s/.test(name)) {
                return `Name "${name}" cannot have spaces in the name.`;
            }
            if (/\s/.test(sequence)) {
                return `Name "${name}" cannot have spaces in the sequence.`;
            }

            if (name in nameToRowNumber) {
                return `Name "${name}" must be unique across rows.`;
            }

            const invalidSequenceChars = findInvalidSequenceCharacters(sequence, setting.config);
            if (invalidSequenceChars.length > 0) {
                return `Name "${name}" contains invalid sequence character ("${invalidSequenceChars[0]}")`;
            }

            nameToRowNumber[name] = rowNumber;
        }
    }
    
    if (isEmpty && setting.required && !setting.default_value && !setting.config?.allow_empty_sequence) {
        return 'Please fill out at least one row.';
    }
    return null;
}
